/**
 * @fileoverview The season review, on one page and as a picture to share: "Your season so
 * far" while the season runs, "Your 2026 season" once it has ended. What
 * the season is, whether it has ended, what it held and every sentence on
 * the page are the backend's (the season brief, GET /season?year=&choice=);
 * this page lays them out, draws the picture and keeps the share sheet and
 * the Public switch.
 *
 * At the top, what counts as the season, the gardener's choice: the growing
 * season (last spring frost to first fall frost, at their typical dates) or
 * the calendar year. The growing season unless they choose; the choice is
 * kept on their account (seasonReviewBy in their preferences, saved through
 * PUT /user-settings/preferences; audit A72), so a second phone shows the
 * same one, and the server reads it with the brief (the page sends a choice
 * only while a new one is being saved, then reads the brief again once it is
 * kept: W24 Job H). A choice that could not
 * be saved goes back to the kept one and says so beside the choice (for a
 * failure on our side, reported to Sentry with the standard sentence).
 *
 * Then what the season held, in words: the plants grown and the beds they
 * grew in, the first planting and the first harvest (each only once there
 * was one), and the photos taken. Grow's weather warnings are not among
 * them: which plants they were for is not kept.
 *
 * Then the season card (seasonFrame, from the plant share's pieces), shown
 * at the size last shared at, with a few photos spread across the season
 * (the brief's startPhotos), and one Share. Share opens the plant share's
 * own sheet for the season (SharePlantDialog's season): the card first,
 * then a picture of each photo chosen, up to the server's limits.SHARE_MAX_PHOTOS pictures in
 * all, in the plant layouts (Peek, Full, Card, Before & after), with the
 * size, the words and the handle chosen there. Everything goes out on one
 * season link, saved at its first use, so the page behind it shows the
 * same pictures. A link made while the season runs is saved as "so far"
 * (soFar), and its page says "So far, as of" the day it was made.
 *
 * Last, the season's Public page switch: on, the page behind its links
 * shows the name the gardener chose; off, the pictures alone. Never an
 * address, a town, a postcode or a note, and it can be switched off at any
 * time. Turned on for the first time ever, it asks, as a plant's does.
 *
 * In someone else's garden (B37; owner, 2026-10-20) the Public switch is the
 * owner's alone: greyed for a member, with "Only the garden's owner can change
 * this." under it (useOwnerOnly); and the preview's handle is the owner's,
 * never the member's (useShareHandles).
 *
 * Reached from the entry on Your Seasons (SeasonReviewEntry) and its asks
 * (SeasonMoments), at /season/review?year=2026; the address works for any
 * year from 2000 to the one under way, whether or not the season has
 * enough to be offered (the backend picks the year).
 *
 * Where: the route /season/review?year= (App.tsx).
 * API: GET /season?year=&choice= (useSeason); PUT
 * /user-settings/preferences { seasonReviewBy } (useUserConfig
 * savePreferences); the plants, and GET /plants?status=0 only once Share is
 * tapped (W24 Job H: not when the page opens), for the photos of removed
 * plants the sheet offers: the sheet opens once that read has finished (its
 * starting photos are picked as it opens), Share saying "Preparing" meanwhile;
 * a read that failed is reported to Sentry and the sheet opens anyway, with
 * the photos it has. A share draft put back after a reload waits the same way;
 * the photos
 * for the picture (GET /photos/:plantId/:photoId/file, through
 * shareFrames); GET /shares/season/:year and PUT /shares/season/:year
 * { isPublic }; from the sheet, at its first Share, Save or Copy: POST
 * /shares (with season, soFar, layout 'season'), POST /shares/:code/used,
 * POST /shares/:code/frame/0 to /frame/n (the card first).
 * Settings: the season choice on the account (preferences.seasonReviewBy);
 * the share size last used on a plant (readShareStyle); the account's
 * share_mark; SEASON_REVIEW_MAX_PHOTOS,
 * SEASON_REVIEW_REDRAW_DELAY_MS
 * (config/tunables/seasonReview); the server's limits.SHARE_MAX_PHOTOS (lib/growSettings).
 * Words (en/es): seasonReview.back, seasonReview.loading, seasonReview.choiceFailed,
 * seasonReview.picture.title, seasonReview.picture.noPhotos,
 * seasonReview.sheet.*, seasonReview.public.*, garden.ownerOnly; publicConsent.* (the
 * first-time dialog's bullets); plants.share.* (the share sheet's own
 * words); sitewide.cancel. The season's own words are the brief's.
 * Watch out: the card is drawn again a moment after what it shows changes
 * (SEASON_REVIEW_REDRAW_DELAY_MS), the previous one shown dimmed meanwhile.
 * The year is the brief's (review.year), never the phone's: before the brief
 * arrives the page waits, and a share draft is looked for under the year in
 * the address, or once the brief names it.
 * Failures on our side (D31, lib/serverFailure) say the standard sentence,
 * reported to Sentry, with the support address as a link: a season or
 * plants that could not be read, a photograph the picture could not fetch,
 * and the Public switch (in place of seasonReview.public.failed's "Try
 * again"). The link's own saves (in the sheet) stay quiet but are reported.
 *
 * @module SeasonReviewPage
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronLeft, Leaf, Sprout, Wheat, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useAuth } from '@/shared/hooks/useAuth';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { setSeasonPublic } from '@/shared/api/shares';
import { useArchivedPlants } from '@/shared/hooks/useArchivedPlants';
import { reportServerFailure } from '@/shared/lib/serverFailure';
import { shareCountKey, useShareCount } from '@/shared/hooks/useShareCount';
import type { SeasonBy } from '@/shared/api/season';
import { useShareHandles } from '@/features/share/useShareHandles';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';
import { toast } from '@/shared/components/ui/use-toast';
import Spinner from '@/shared/components/ui/Spinner';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { Chip } from '@/shared/components/ui/Chip';
import { Pill } from '@/shared/components/ui/Pill';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Button } from '@/shared/components/ui/button';
import { ShareActionButton } from '@/features/share/ShareButtons';
import { Switch } from '@/shared/components/ui/switch';
import { PublicConsentBullets } from '@/features/share/PublicConsentBullets';
import { usePublicSwitch } from '@/features/share/usePublicSwitch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SHARE_SIZES, type ShareSize } from '@/features/plant-list/lib/shareFrames';
import { readShareStyle } from '@/features/plant-list/lib/shareStyle';
import { SharePlantDialog, type SeasonShare } from '@/features/plant-list/components/dialogs/SharePlantDialog';
import type { PlantPhoto } from '@/shared/types/photo';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { shareWords } from '@/shared/lib/plantName';
import { SEASON_QUERY_KEY, SEASON_REVIEW_MAX_PHOTOS, SEASON_REVIEW_REDRAW_DELAY_MS } from '@/config/tunables/seasonReview';
import { useSeason } from './lib/useSeason';
import { buildSeasonFrame, type SeasonFrameInput } from './lib/seasonFrame';
import axios from 'axios';
import { errorMessage } from '@/shared/lib/errorMessage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { FailureText } from '@/shared/components/ServerFailure';
import { shareDraftKey, useResumeShare } from '@/features/plant-list/lib/shareDraft';

/** The frame each section sits in, as on Your Seasons. */
const CARD = 'rounded-lg border bg-card p-4';
/** A section's small heading. */
const SECTION_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
/** Each "what it held" row's icon, by its key. */
const HELD_ICONS: Record<'plants' | 'planting' | 'harvest' | 'photos', LucideIcon> = { plants: Sprout, planting: Leaf, harvest: Wheat, photos: Camera };

/** The season review page at /season/review. */
export const SeasonReviewPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const maxPhotos = useGrowLimits().SHARE_MAX_PHOTOS;
  const { plants, isLoading, error: plantsError } = usePlantsContext();
  const { mysqlUser } = useAuth();
  const userId = mysqlUser?.user_id ?? null;
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const { userConfig, savePreferences } = useUserConfig();
  /** A choice of season just made and still being saved: shown and read at once. */
  const [pendingBy, setPendingBy] = React.useState<SeasonBy | null>(null);
  /** Why the last choice could not be saved, in words; null when it was. */
  const [byFailure, setByFailure] = React.useState<string | null>(null);
  /** What counts as the season: a choice being saved, else the one kept on the account (the server's when not yet read). */
  const by = pendingBy ?? userConfig.preferences.seasonReviewBy;
  /**
   * Chooses what counts as the season and keeps it on the account. The brief
   * is read for the new choice at once; once it is kept, the kept brief is
   * marked stale, so it is read again under the server's kept choice. A save
   * that fails goes back to the kept choice and says why; a failure on our
   * side is reported to Sentry and says the standard sentence.
   *
   * @param next - growing or calendar.
   */
  const chooseBy = (next: SeasonBy): void => {
    setPendingBy(next);
    setByFailure(null);
    savePreferences({ ...userConfig.preferences, seasonReviewBy: next })
      .then(() => { void queryClient.invalidateQueries({ queryKey: [SEASON_QUERY_KEY], refetchType: 'none' }); })
      .catch((err: unknown) => {
        setByFailure(serverFailureText(err, 'seasonReview.choice') ?? errorMessage(err, t('seasonReview.choiceFailed')));
      })
      .finally(() => setPendingBy(null));
  };

  // The year asked for in the address; the backend takes it, else the season offered, else the one under way.
  const askedYear = Number(params.get('year'));
  /** The year asked for in the address, or null for none (the server picks); no ?year= was read as year 0 before. */
  const addressYear = params.get('year') && Number.isInteger(askedYear) && askedYear > 0 ? askedYear : null;
  // Only a choice being saved is sent; the kept one is the server's to read (one read on an open).
  const { brief, loading: seasonLoading, error: seasonError } = useSeason({ year: addressYear, choice: pendingBy });

  /** Share was tapped (or a draft put back): the sheet is wanted, and opens once the removed plants are read. */
  const [sheetAsked, setSheetAsked] = React.useState(false);
  // The plants taken out, read once Share is tapped, so their season photos can go to the sheet as a plant's do.
  const archive = useArchivedPlants(sheetAsked);
  const archived = React.useMemo(() => archive.data ?? [], [archive.data]);
  /**
   * The share sheet, a plant's, for the season: its card first, then a picture of each photo. Open once the removed
   * plants' read has finished (answered or failed), so the photos it starts with include theirs.
   */
  const sheetOpen = sheetAsked && (archive.isSuccess || archive.isError);
  /** Share is waiting for the removed plants' read. */
  const sheetPreparing = sheetAsked && !sheetOpen;
  // A failed read is reported (the error standard); the sheet opens anyway with the photos it has.
  React.useEffect(() => {
    if (archive.error) reportServerFailure(archive.error, 'seasonReview.shareArchive');
  }, [archive.error]);
  /** Asks for the sheet: Share tapped, or a share draft put back after a reload. */
  const askSheet = React.useCallback(() => setSheetAsked(true), []);
  /** The live garden and the plants taken out of it. */
  const all = React.useMemo(() => [...(plants ?? []), ...archived], [plants, archived]);

  const review = brief?.review ?? null;
  /** The season's year: the brief's, else the address's until the brief arrives; null while neither names one. */
  const year = review?.year ?? addressYear;
  const ended = review?.ended ?? false;
  const nothing = review?.nothing ?? true;

  /* ----------------------------------------------------------- picture */
  // A share of this season in progress when the tab was reloaded: its sheet opens again where it was (once a year is known).
  useResumeShare(year === null ? null : shareDraftKey('season', year), askSheet);
  /** The preview's size: the one last shared at, read again whenever the sheet closes (it keeps its own). */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const size = React.useMemo<ShareSize>(() => readShareStyle().size, [sheetOpen]);
  /** A few photos spread across the season (the brief's startPhotos): the preview's, and the sheet's to begin with. */
  const pickedIds = React.useMemo(() => review?.startPhotos ?? [], [review]);
  /** The handles: their own links in their garden, the owner's in someone else's (B37); the first is the preview's. */
  const { links } = useShareHandles();
  /** The preview's handle: their first link, or none. */
  const handle = React.useMemo(
    () => (links[0] ? { text: links[0].handle, place: links[0].key } : null),
    [links],
  );
  // Whether the Grow mark heads the words: not on an account the Grow settings leave it off.
  const showMark = mysqlUser?.share_mark !== false;

  /** Everything the picture prints, in words: the brief's frame words, the photos chosen and the handle. */
  const frameInput = React.useMemo<SeasonFrameInput | null>(() => (review ? {
    year: review.year,
    size,
    title: review.frame.title.text,
    spanLine: review.frame.spanLine.text,
    stats: review.frame.stats,
    lines: review.frame.lines.map((line) => line.text),
    words: '',
    photos: pickedIds
      .map((id) => review.candidates.find((each) => each.photoId === id))
      .filter((each): each is NonNullable<typeof each> => Boolean(each))
      .map((each) => ({ plantObjId: each.plantObjId, photoId: each.photoId })),
    handle,
    showMark,
  } : null), [review, size, pickedIds, handle, showMark]);
  /** The picture's choices as one key: a finished picture is for this key only. */
  const frameKey = JSON.stringify(frameInput);

  /** The last finished picture and what it was made from. */
  const [built, setBuilt] = React.useState<{ key: string; file: File } | null>(null);
  /** A picture that could not be made, and why. */
  const [buildError, setBuildError] = React.useState<{ key: string; message: string } | null>(null);
  React.useEffect(() => {
    if (!frameInput || nothing || isLoading) return;
    let cancelled = false;
    // Waits for the taps and the typing to stop, then draws once.
    const timer = setTimeout(() => {
      buildSeasonFrame(frameInput)
        .then((file) => { if (!cancelled) setBuilt({ key: frameKey, file }); })
        .catch((err) => {
          if (cancelled) return;
          // A photograph that could not be fetched is a failed request, said
          // as one (the standard sentence for a failure on our side); a
          // drawing that failed on the device keeps its own words.
          setBuildError({
            key: frameKey,
            message: axios.isAxiosError(err)
              ? errorMessage(err, t('plants.share.failed'))
              : err instanceof Error ? err.message : t('plants.share.failed'),
          });
        });
    }, SEASON_REVIEW_REDRAW_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // The key stands in for the input, made again every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameKey, nothing, isLoading]);
  /** The picture for the choices as they stand, or null while it is being made. */
  const file = built?.key === frameKey ? built.file : null;
  const drawing = !file && buildError?.key !== frameKey;
  /** The last picture made, shown (dimmed) while the next is drawn. */
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!built) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(built.file);
    setPreviewUrl(url);
    return () => { URL.revokeObjectURL(url); };
  }, [built]);

  /** The season's Public switch and its numbers. */
  const seasonSubject = React.useMemo(() => ({ kind: 'season', id: year ?? 0 } as const), [year]);
  const sharingKey = React.useMemo(() => shareCountKey(seasonSubject), [seasonSubject]);
  const { sharing, error: sharingError } = useShareCount(seasonSubject, userId !== null && review !== null);
  /** The switch while the season has no link yet: kept here, and sent with the first one. */
  const [wantPublic, setWantPublic] = React.useState(false);
  const hasLinks = (sharing?.shares ?? 0) > 0;
  const isPublic = hasLinks ? Boolean(sharing?.isPublic) : wantPublic;

  /* ------------------------------------------------------------- sheet */
  /** Each candidate's photo, as the plant carries it (its signed links), by photo id; a photo the garden no longer has is left out. */
  const candidatePhotos = React.useMemo(() => {
    const out = new Map<number, { photo: PlantPhoto; plantObjId: number; plantName: string }>();
    const byPlant = new Map(all.map((plant) => [plant.plant_obj_id, plant]));
    for (const each of review?.candidates ?? []) {
      const photo = ((byPlant.get(each.plantObjId)?.photos ?? []) as unknown as PlantPhoto[]).find((p) => p.photo_id === each.photoId);
      if (photo) out.set(each.photoId, { photo, plantObjId: each.plantObjId, plantName: each.plantName });
    }
    return out;
  }, [all, review]);
  /** The season's plant photos for the sheet, their ids made negative (the card is 0). */
  const sheetPhotos = React.useMemo<PlantPhoto[]>(
    () => [...candidatePhotos.values()].map((each) => ({ ...each.photo, photo_id: -each.photo.photo_id })),
    [candidatePhotos],
  );
  /** Each season photo's plant, by its negative id: its crop and variety, as a plant's share prints them. */
  const sheetPlants = React.useMemo(() => {
    const byPlant = new Map(all.map((plant) => [plant.plant_obj_id, plant]));
    return new Map([...candidatePhotos.values()].map((each) => {
      const plant = byPlant.get(each.plantObjId);
      // The server's names, as a plant's share prints them (C118 F2: no screen builds a name, and none tests a Type "Not sure"); the review's own plant name when the plant is gone from the list.
      const words = plant ? shareWords(plant) : { name: each.plantName, variety: '' };
      return [-each.photo.photo_id, { name: words.name || each.plantName, variety: words.variety, plantObjId: each.plantObjId }];
    }));
  }, [all, candidatePhotos]);
  /**
   * The plant a season photo in the sheet is of.
   *
   * @param photoId - The photo's id in the sheet (negative).
   * @returns Its plant's names and id, or null for the card.
   */
  const sheetPlantOf = React.useCallback((photoId: number) => sheetPlants.get(photoId) ?? null, [sheetPlants]);
  /** The sheet opens with the card and the photos on the picture here. */
  const sheetStart = React.useMemo(() => [0, ...pickedIds.map((id) => -id)], [pickedIds]);
  /** What the sheet needs to make the season's card and save its link. */
  const seasonShare = React.useMemo<SeasonShare | null>(() => (frameInput && review ? {
    year: review.year,
    soFar: !ended,
    isPublic,
    cardKey: JSON.stringify([frameInput.title, frameInput.spanLine, frameInput.stats, frameInput.lines, frameInput.showMark]),
    buildCard: (choices) => buildSeasonFrame({
      ...frameInput,
      size: choices.size,
      words: choices.words.trim(),
      handle: choices.handle,
      photos: choices.photos.slice(0, SEASON_REVIEW_MAX_PHOTOS),
    }),
    caption: review.frame.caption.text,
    onShared: () => { void queryClient.invalidateQueries({ queryKey: sharingKey }); },
  } : null), [ended, isPublic, frameInput, review, queryClient, sharingKey]);

  /* ------------------------------------------------------------ public */
  /** Turns the season's Public switch on or off, for its links and for the next one. */
  const toggle = useMutation({
    mutationFn: (on: boolean) => setSeasonPublic(review?.year ?? 0, on),
    onSuccess: (_, on) => {
      publicSwitch.saved(on);
      setWantPublic(on);
      queryClient.setQueryData(sharingKey, (current: typeof sharing) => (current ? { ...current, isPublic: on, needsConsent: current.needsConsent && !on } : current));
      void queryClient.invalidateQueries({ queryKey: sharingKey });
    },
  });
  /** In someone else's garden: why the season's Public switch is locked (B37); null in the person's own. */
  const { lockedReason } = useOwnerOnly();
  /**
   * A press of the Public switch: for a member, only why it is locked (a toast); otherwise the first question or the change.
   *
   * @param on - The state pressed for.
   */
  const pressPublic = (on: boolean): void => {
    if (lockedReason) {
      toast({ description: lockedReason });
      return;
    }
    publicSwitch.press(on);
  };
  /** The first question (the consent), as the server says it: usePublicSwitch. */
  const publicSwitch = usePublicSwitch({
    sharing,
    readError: sharingError,
    where: 'SeasonReviewPage',
    apply: (on, _galleryName, onSettled) => toggle.mutate(on, onSettled ? { onSettled } : undefined),
  });

  if (isLoading || (seasonLoading && !brief)) {
    return <GrowLoader variant="screen" label={t('seasonReview.loading')} />;
  }
  // Plants or a season that could not be read are said so, as Your Seasons
  // says it, rather than an empty season.
  if (plantsError && (plants ?? []).length === 0) {
    return <p role="alert" className="py-16 text-center text-sm text-destructive"><FailureText text={errorMessage(plantsError, t('plants.list.loadFailed'))} /></p>;
  }
  if (!brief || !review || !frameInput) {
    return <p role="alert" className="py-16 text-center text-sm text-destructive"><FailureText text={errorMessage(seasonError, t('plants.list.loadFailed'))} /></p>;
  }

  const { w, h } = SHARE_SIZES[size];
  /** The preview could not be made, and why. */
  const buildErrorShown = buildError?.key === frameKey ? buildError.message : null;

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to="/season" className="inline-flex min-h-9 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />{t('seasonReview.back')}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground font-body">
        {review.title.text}
      </h1>

      {/* What counts as the season: the gardener's to say. */}
      <section className={cn(CARD, 'mt-3')} aria-labelledby="season-by">
        <p id="season-by" className={SECTION_TITLE}>{brief.choice.label.text}</p>
        <div role="radiogroup" aria-labelledby="season-by" className="mt-2 grid grid-cols-2 gap-1.5">
          {brief.choice.values.map((option) => {
            const on = (review.span.fellBack ? (by ?? brief.choice.by) : review.span.by) === option.value;
            return (
              <Chip key={option.value} radio pressed={on} onClick={() => chooseBy(option.value)} className="w-full">
                {option.text}
              </Chip>
            );
          })}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {review.days.text}
          {review.soFar && ` ${review.soFar.text}`}
        </p>
        {byFailure && (
          <p role="alert" className="mt-2 text-sm text-destructive"><FailureText text={byFailure} /></p>
        )}
      </section>

      {/* What it held. */}
      <section className={cn(CARD, 'mt-3')} aria-labelledby="season-held">
        <h2 id="season-held" className={SECTION_TITLE}>{t('seasonReview.held.title')}</h2>
        {nothing ? (
          <p className="mt-2 text-sm">{review.nothingText.text}</p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-[0.9375rem]">
            {review.held.map((row) => {
              /** The row's icon, by its key. */
              const Icon = HELD_ICONS[row.key];
              return (
                <li key={row.key} className="flex items-center gap-2.5">
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />{row.text}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {!nothing && (
        <>
          {/* The picture, made as a plant's is. */}
          <section className={cn(CARD, 'mt-3')} aria-labelledby="season-picture">
            <h2 id="season-picture" className={SECTION_TITLE}>{t('seasonReview.picture.title')}</h2>
            <div className="relative mt-2 flex justify-center rounded-md bg-muted/50 p-2">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={t('plants.share.previewAlt')}
                  className={cn('h-auto max-h-[60vh] w-auto max-w-full rounded-sm shadow-md transition-opacity', drawing && 'opacity-75')}
                />
              ) : (
                <span className="flex max-h-[60vh] w-full max-w-xs items-center justify-center rounded-sm bg-muted" style={{ aspectRatio: `${w} / ${h}` }}>
                  <Spinner size={20} />
                </span>
              )}
              {drawing && previewUrl && (
                <Pill size="sm" tone="photo" role="status" icon={<Spinner size={12} color="white" />} className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2">
                  {t('plants.share.preparing')}
                </Pill>
              )}
            </div>

            <p className="mt-2 text-sm text-muted-foreground">
              {review.candidates.length > 0
                ? (maxPhotos === undefined ? '' : t('seasonReview.sheet.hint', { count: maxPhotos - 1 }))
                : t('seasonReview.picture.noPhotos')}
            </p>
            {buildErrorShown && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-destructive"><X className="h-4 w-4 shrink-0" aria-hidden="true" /><span><FailureText text={buildErrorShown} /></span></p>
            )}
            {/* The plant share's sheet, for the season: the card, then a picture of each photo, on one link. */}
            <ShareActionButton className="mt-3 w-full" onClick={askSheet} disabled={sheetPreparing} />
            {/* The removed plants' photos are being read before the sheet opens. */}
            {sheetPreparing && (
              <p role="status" className="mt-2 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Spinner size={14} />{t('plants.share.preparing')}
              </p>
            )}
          </section>

          {/* The season's Public page: what its links' page shows, and the switch. */}
          <section className={cn(CARD, 'mt-3')} aria-labelledby="season-public">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 id="season-public" className="font-medium">{t('seasonReview.public.label')}</h2>
                <p className="text-sm text-muted-foreground">{t(isPublic ? 'seasonReview.public.on' : 'seasonReview.public.off')}</p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={pressPublic}
                disabled={toggle.isPending || !sharing}
                aria-disabled={lockedReason ? true : undefined}
                title={lockedReason ?? undefined}
                aria-labelledby="season-public"
                className={cn('mt-1', lockedReason && 'opacity-50')}
              />
            </div>
            {/* A member: only the garden's owner turns it (B37). */}
            {lockedReason && <p className="mt-2 text-sm text-muted-foreground">{lockedReason}</p>}
            <p className="mt-2 text-xs text-muted-foreground">{t('seasonReview.public.never')}</p>
            {(sharing?.visits ?? 0) > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {sharing?.visits === 1 ? t('seasonReview.public.visits_one') : t('seasonReview.public.visits', { count: sharing?.visits ?? 0 })}
              </p>
            )}
            {toggle.isError && (
              <p className="mt-1 text-sm text-destructive">
                <FailureText text={serverFailureText(toggle.error, 'seasonReview.public') ?? t('seasonReview.public.failed')} />
              </p>
            )}
          </section>
        </>
      )}

      {!nothing && seasonShare && (
        <SharePlantDialog
          isOpen={sheetOpen}
          onClose={() => setSheetAsked(false)}
          plantObjId={review.year}
          // The share's title and the saved files' names are the reader's, so the card's own title, never "Your season so far".
          name={frameInput.title}
          variety={frameInput.title}
          title={t('seasonReview.sheet.title')}
          photos={sheetPhotos}
          startPhotoIds={sheetStart}
          plantOf={sheetPlantOf}
          season={seasonShare}
        />
      )}

      {/* Asked once, the first time anything of theirs shows their name. */}
      <Dialog open={publicSwitch.asking} onOpenChange={publicSwitch.setAsking}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('seasonReview.public.consentTitle')}</DialogTitle>
            <PublicConsentBullets kind="season" />
          </DialogHeader>
          <DialogFooter>
            <ActionRow onCancel={() => publicSwitch.setAsking(false)} disabled={toggle.isPending}>
              <Button loading={toggle.isPending} onClick={() => publicSwitch.confirm()}>{t('seasonReview.public.consentAction')}</Button>
            </ActionRow>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** The season review page, as the route's default import. */
export default SeasonReviewPage;
